import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../../data/site";

const Logo = () => (
  <Link to="/" data-testid="header-logo" className="flex items-center" aria-label="PM1 Clean — Precision Maintenance1 LLC home">
    <img src="/pm1-logo.png" alt="PM1 Clean — Precision Maintenance1 LLC" className="h-9 w-auto sm:h-10" width="640" height="201" />
  </Link>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b bg-white/85 backdrop-blur-xl transition-shadow duration-300"
      style={{ borderColor: scrolled ? "hsl(var(--border))" : "transparent", boxShadow: scrolled ? "0 1px 20px rgba(0,0,0,0.06)" : "none" }}
    >
      <div className="container-px mx-auto flex h-20 max-w-[1500px] items-center justify-between">
        <Logo />

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `link-underline font-display text-[13px] font-medium uppercase tracking-wide transition-colors duration-200 ${
                  isActive ? "text-brand-green-ink" : "text-navy/80 hover:text-navy"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/request-a-quote" data-testid="header-quote-cta" className="btn-primary hidden sm:inline-flex">
            Request a Quote
            <ArrowUpRight size={16} strokeWidth={2.4} />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="flex h-11 w-11 items-center justify-center border border-navy/15 text-navy transition-colors duration-200 hover:bg-navy hover:text-white lg:hidden"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
    </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-x-0 bottom-0 top-20 z-40 bg-navy lg:hidden"
          >
            <nav className="container-px flex h-[calc(100vh-5rem)] flex-col justify-between py-10" aria-label="Mobile">
              <div className="flex flex-col">
                {NAV_LINKS.map((l, i) => (
                  <motion.div
                    key={l.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i + 0.1, duration: 0.4 }}
                  >
                    <Link
                      to={l.path}
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="flex items-center justify-between border-b border-white/10 py-4 font-display text-2xl font-semibold uppercase tracking-tight text-white"
                    >
                      {l.label}
                      <ArrowUpRight size={22} className="text-brand-green" />
                    </Link>
                  </motion.div>
                ))}
              </div>
              <Link to="/request-a-quote" data-testid="mobile-quote-cta" className="btn-primary w-full">
                Request a Quote
                <ArrowUpRight size={16} />
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
